<template>
  <div>
    <b-card v-bind:class="{ spinner: isBusy }">
      <template slot="header">
        <treeselect
          instance-id="client-users"
          v-model="clientUsersValue"
          :auto-load-root-options="false"
          :placeholder="$t('Webportal.ClientUsers.Placeholder')"
          :options="clientUsers"
          :disabled="disabled"
          :disableFuzzyMatching="true"
          :multiple="true"
          value-format="object"
          :showCount="true"
          showCountOf="LEAF_DESCENDANTS"
          :limit="20"
          :limit-text="limitText">
          <template #option-label="{ node, shouldShowCount, count, countClassName }">
            <div v-if="node.isBranch">{{ node.label }} <span v-if="shouldShowCount" :class="countClassName">({{ count }})</span></div>
            <div v-else class="row">
              <div class="col">
                {{ node.raw.FirstName }} {{ node.raw.LastName }}
                <span v-if="node.raw.Email">
                  ({{ node.raw.LoginName }} / {{ node.raw.Email }})
                </span>
                <b-badge v-if="node.raw.HasPermission === false" variant="warning"
                  v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.DefaultNotifications.NoPermission.Description')">
                  <font-awesome-icon :icon="['far', 'exclamation-circle']" /> {{ $t('Webportal.DefaultNotifications.NoPermission') }}
                </b-badge>
              </div>
              <div v-if="node.raw.LastLogin" class="col-auto ml-auto">
                {{ $t('Webportal.LastLogin') }}: {{ node.raw.LastLogin | msm_date }}
              </div>
            </div>
          </template>
        </treeselect>
      </template>

      <div class="form-group">
        <b-form-radio-group buttons button-variant="outline-primary"
                            v-model="workflowEventTypeValue">
          <b-form-radio :value="WorkflowEventType.CheckReport">{{ $t('Webportal.CheckReport') }}</b-form-radio>
          <b-form-radio :value="WorkflowEventType.CheckRevision">{{ $t('Webportal.Actions.SurveyRevision') }}</b-form-radio>
          <b-form-radio v-if="hasSurveyImprovement()" :value="WorkflowEventType.CheckImprovement">{{ $t('Webportal.Actions.SurveyImprovement') }}</b-form-radio>
        </b-form-radio-group>
      </div>

      <b-form-group :label-cols-sm="2" :label="$t('Webportal.Notification')" label-class="font-weight-bold pt-0"
                    :disabled="disabled">
        <b-form-checkbox-group v-model="currentNotification.WorkflowEventIds">
          <template v-if="workflowEventTypeValue == WorkflowEventType.CheckReport">
            <b-form-checkbox :value="1">
              {{ $t("Webportal.NotificationSettings.NotifyOnNewChecks") }}
            </b-form-checkbox>
            <b-form-checkbox :value="2">
              {{ $t("Webportal.NotificationSettings.NotifyOnModifiedChecks") }}
            </b-form-checkbox>
          </template>
          <template v-if="workflowEventTypeValue == WorkflowEventType.CheckRevision">
            <b-form-checkbox :value="11">
              {{ $t("Webportal.NotificationSettings.NotifyOnNewCheckRevisions") }}
            </b-form-checkbox>
            <b-form-checkbox :value="12">
              {{ $t("Webportal.NotificationSettings.NotifyOnModifiedCheckRevisions") }}
            </b-form-checkbox>
          </template>
          <template v-if="workflowEventTypeValue == WorkflowEventType.CheckImprovement">
            <b-form-checkbox :value="20">
              {{ $t("Webportal.NotificationSettings.NotifyOnActionsToTakeCheckImprovements") }}
            </b-form-checkbox>
            <b-form-checkbox :value="21">
              {{ $t("Webportal.NotificationSettings.NotifyOnNewCheckImprovements") }}
            </b-form-checkbox>
            <b-form-checkbox :value="22">
              {{ $t("Webportal.NotificationSettings.NotifyOnModifiedCheckImprovements") }}
            </b-form-checkbox>
          </template>
        </b-form-checkbox-group>
      </b-form-group>

      <b-form-group :label-cols-sm="2" :label="$t('Webportal.Schedule')" label-class="font-weight-bold pt-0"
                    :disabled="disabled">
        <schedule-input v-model="currentNotification.CronScheduleUtc" />
      </b-form-group>

      <template #footer>
        <div class="float-right">
          <b-button variant="primary" @click="applyToSelected" :disabled="isBusy">
            <font-awesome-icon :icon="['fas', 'share-all']" /> {{ $t('Webportal.DefaultNotifications.ApplyToSelected') }}
          </b-button>
        </div>
      </template>
    </b-card>
  </div>
</template>

<script>
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import ScheduleInput from "@/components/ScheduleInput";
import AdministationService from '@/services/AdministationService';

const WorkflowEventType = {
  CheckReport: 1,
  CheckRevision: 2,
  CheckImprovement: 3
}

export default {
  name: "notificationSettings",

  mixins: [ AppConfig ],

  props: {
    clientId: { type: Number, default: null },
    disabled: { type: Boolean, default: false }
  },

  components: {
    ScheduleInput,
  },

  data() {
    return {
      isBusy: false,

      WorkflowEventType: WorkflowEventType,
      workflowEventTypeValue: WorkflowEventType.CheckReport,

      clientUsersValue: [],
      clientUsers: [],

      defaultNotifications: [],
    }
  },

  computed: {
    currentNotification() {
      return this.defaultNotifications.find(n => 
        n.Name === (this.workflowEventTypeValue == WorkflowEventType.CheckReport ?
          'Check Reports' :
          (this.workflowEventTypeValue == WorkflowEventType.CheckRevision ?
            'Check Revisions' :
            'Check Improvements')
          )) ?? {};
    },
  },

  watch: {
    clientId() {
      this.getDefaultNotifications();
    }
  },

  mounted() {
    this.initializeTheme();
    this.getDefaultNotifications(this.clientId);
  },

  methods: {
    getDefaultNotifications() {
      this.isBusy = true;

      this.clientUsersValue = [];
      this.clientUsers = [];

      AdministationService.getDefaultNotifications(this.clientId, true)
        .then(response => {
          this.defaultNotifications = response.data.DefaultNotifications ?? [];

          if (!this.defaultNotifications.find(n => n.Name === 'Check Reports'))
            this.defaultNotifications.push({
              Name: 'Check Reports',
              NotificationTypeId: 1,
              CronScheduleUtc: "0 0 17 ? * *",
              WorkflowEventIds: [],
            });

          if (!this.defaultNotifications.find(n => n.Name === 'Check Revisions'))
            this.defaultNotifications.push({
              Name: 'Check Revisions',
              NotificationTypeId: 1,
              CronScheduleUtc: "0 0 17 ? * *",
              WorkflowEventIds: [],
            });

          if (!this.defaultNotifications.find(n => n.Name === 'Check Improvements'))
            this.defaultNotifications.push({
              Name: 'Check Improvements',
              NotificationTypeId: 1,
              CronScheduleUtc: "0 0 17 ? * *",
              WorkflowEventIds: [],
            });

          var userNotificationMapping = {
            'true': { id: 'N1', label: this.$t('Webportal.DefaultNotifications.UsersWithDefaultNotifications') },
            'false': { id: 'N2', label: this.$t('Webportal.DefaultNotifications.UsersWithCustomizedNotifications') },
            undefined: { id: 'N0', label: this.$t('Webportal.DefaultNotifications.UsersWithoutNotifications') }
          };

          if (response.data.Users)
          {
            this.clientUsers = _(response.data.Users)
              .groupBy(u => u.IsDefaultNotification)
              .map((items, key) => ({ 
                id: userNotificationMapping[key].id,
                label: userNotificationMapping[key].label,
                children: items.map(u => ({
                  id: u.UserId,
                  label: `${u.FirstName} ${u.LastName}`,
                  isDisabled: u.IsDefaultNotification ? true : false,
                  HasPermission: u.HasPermission,
                  LoginName: u.LoginName,
                  FirstName: u.FirstName,
                  LastName: u.LastName,
                  Email: u.Email,
                  LastLogin: u.LastLogin
                }))
              }))
              .value();

            this.clientUsersValue = this.clientUsers.filter(m => m.id == 'N0' || m.id == 'N1');
          }
        })
        .finally(_ => {
          this.isBusy = false;
        });
    },

    async applyToSelected() {
      this.isBusy = true;

      try {
        await AdministationService.setDefaultNotifications(
          this.clientId, 
          {
            DefaultNotifications: this.defaultNotifications,
            UserIds: _(this.clientUsersValue)
              .flatMapDeep(item => item.id[0] === 'N' ? item.children : item)
              .map(item => item.id)
              .value()
          });

        this.$bvToast.toast(
          this.$t("Webportal.SaveSuccess.Text", {
            entity: this.$t("Webportal.NotificationSettings"),
          }),
          {
            autoHideDelay: 2000,
            variant: "success",
            noCloseButton: true,
          }
        );

        this.getDefaultNotifications();
      } catch (error) {
        console.error(error);

        this.$bvToast.toast(
          this.$t("Webportal.SaveFailed.Text", {
            entity: this.$t("Webportal.NotificationSettings"),
          }),
          {
            autoHideDelay: 10 * 1000,
            variant: "danger",
            noCloseButton: true,
          }
        );
        throw error;
      } finally {
        this.isBusy = false;
      }
    }
  }
}
</script>
